import type { Account } from '../types/account'
import {
	buildDefaultPrivateHostedZoneName,
	buildDefaultVpcName,
	resolveTagScalar
} from './accountVpcName'

/** Network fields populated from approved Stage Network Details (merged into account details on stg approve). */
export function getClusterNetworkFromAccount(
	account: Account | null | undefined,
	environment: string,
	application: string,
	options?: {
		portfolioPrefix?: string
		systemAlias?: unknown
		distributionList?: string
		accountType?: string
		isStgFlow?: boolean
	}
): { vpcName: string; privateHostedZoneName: string } {
	const env = environment.toLowerCase().trim()
	const systemAlias = resolveTagScalar(options?.systemAlias ?? application)
	const distributionList =
		options?.distributionList ??
		(account?.details as { distributionList?: string } | undefined)?.distributionList
	const accountType = options?.accountType ?? account?.type
	const isStgFlow = options?.isStgFlow ?? env === 'st'
	const portfolioPrefix = (options?.portfolioPrefix ?? '').trim().toLowerCase()

	const readApprovalNetwork = () => {
		const approval = account?.approval as Record<string, unknown> | undefined
		if (!approval) return { vpcName: '', privateHostedZoneName: '' }
		const nested =
			approval.details && typeof approval.details === 'object'
				? (approval.details as Record<string, unknown>)
				: approval
		return {
			vpcName: String(nested.vpcName ?? approval.vpcName ?? '').trim(),
			privateHostedZoneName: String(
				nested.privateHostedZoneName ?? approval.privateHostedZoneName ?? ''
			).trim()
		}
	}

	const fallbackVpc =
		buildDefaultVpcName({
			systemAlias,
			envCode: env,
			distributionList,
			accountType,
			isStgFlow
		}) ||
		(portfolioPrefix
			? `${portfolioPrefix}-${application}-${env}-aws-vpc-1`
			: `${application}-${env}-aws-vpc-1`)
	const fallbackDns =
		buildDefaultPrivateHostedZoneName({
			systemAlias,
			envCode: env,
			accountType,
			isStgFlow
		}) || `${application}-${env}.globetel.cloud`

	if (env !== 'st' || !account) {
		const { vpcName: approvedVpc, privateHostedZoneName: approvedDns } =
			readApprovalNetwork()
		return {
			vpcName: approvedVpc || fallbackVpc,
			privateHostedZoneName: approvedDns || fallbackDns
		}
	}

	const details = (account.details ?? {}) as Record<string, unknown>
	const stgDetails = (account.stgRequest?.details ?? {}) as Record<string, unknown>

	const vpcName = String(details.vpcName ?? stgDetails.vpcName ?? '').trim()
	const privateHostedZoneName = String(
		details.privateHostedZoneName ?? stgDetails.privateHostedZoneName ?? ''
	).trim()

	return {
		vpcName: vpcName || fallbackVpc,
		privateHostedZoneName: privateHostedZoneName || fallbackDns
	}
}
